import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Save, FileText, Calculator, TrendingUp, AlertCircle, Plus, Minus, Search, User, ChevronLeft, CheckCircle } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { apiService } from '../../services/apiService'

const AdminFinancialDataEntry = () => {
  const { admin } = useAdminAuth()
  const [activeTab, setActiveTab] = useState('search')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  // Member search
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedMember, setSelectedMember] = useState(null)
  const [searchLoading, setSearchLoading] = useState(false)

  // Products data for Profit & Loss
  const [products, setProducts] = useState([
    { product_name: '', revenue: '', cost_of_goods_sold: '', gross_profit: '', notes: '' }
  ])

  // Operating expenses data
  const [operatingExpenses, setOperatingExpenses] = useState([
    { category: '', description: '', amount: '' }
  ])

  // Additional P&L data
  const [plData, setPLData] = useState({
    period_start: '', period_end: '', other_income: '', other_expenses: '', net_income: '', notes: ''
  })

  // Balance Sheet Data
  const [bsData, setBSData] = useState({
    as_of_date: '',
    cash_and_equivalents: '', accounts_receivable: '', inventory: '', other_current_assets: '',
    total_current_assets: '', fixed_assets: '', accumulated_depreciation: '', net_fixed_assets: '',
    other_assets: '', total_assets: '',
    accounts_payable: '', short_term_debt: '', other_current_liabilities: '',
    total_current_liabilities: '', long_term_debt: '', other_liabilities: '', total_liabilities: '',
    owner_equity: '', retained_earnings: '', total_equity: '', notes: ''
  })

  const [statementType, setStatementType] = useState('profit-loss')

  const mgKey = admin?.mgCode || admin?.mgKey || admin?.mg_key || 'demo'

  const handleSearchMember = async () => {
    if (!searchQuery.trim()) { setError('Please enter a member number or name'); return }
    setSearchLoading(true)
    setError('')
    setSearchResults([])
    setSelectedMember(null)

    try {
      const response = await apiService.searchCustomers(searchQuery.trim())
      const customers = response.data?.customers || response.data || []
      if (customers.length === 0) {
        setError('No members found. Please check the member number and try again.')
      } else {
        setSearchResults(customers)
      }
    } catch (err) {
      console.error('Search error:', err)
      setError('Failed to search for member. Please try again.')
    } finally {
      setSearchLoading(false)
    }
  }

  const selectMember = (member) => {
    setSelectedMember({
      ...member,
      memberNo: member.client_id || member.mem_no || member.memberNo || member.member_number,
      fullName: member.client_name || member.fullName || member.full_name,
      customerId: member.id || member.customerId || member.customer_id
    })
    setActiveTab('profit-loss')
    setSuccess(`Selected member: ${member.client_name || member.fullName || member.full_name}`)
  }

  // Product management
  const addProduct = () => {
    setProducts([...products, { product_name: '', revenue: '', cost_of_goods_sold: '', gross_profit: '', notes: '' }])
  }
  const removeProduct = (index) => {
    if (products.length > 1) setProducts(products.filter((_, i) => i !== index))
  }
  const handleProductChange = (index, field, value) => {
    const updated = [...products]
    updated[index][field] = value
    if (field === 'revenue' || field === 'cost_of_goods_sold') {
      const revenue = Math.round(parseFloat(updated[index].revenue) || 0)
      const cogs = Math.round(parseFloat(updated[index].cost_of_goods_sold) || 0)
      updated[index].gross_profit = (revenue - cogs).toString()
    }
    setProducts(updated)
  }

  // Expense management
  const addExpense = () => {
    setOperatingExpenses([...operatingExpenses, { category: '', description: '', amount: '' }])
  }
  const removeExpense = (index) => {
    if (operatingExpenses.length > 1) setOperatingExpenses(operatingExpenses.filter((_, i) => i !== index))
  }
  const handleExpenseChange = (index, field, value) => {
    const updated = [...operatingExpenses]
    if (field === 'amount' && value !== '') {
      updated[index][field] = Math.round(parseFloat(value) || 0).toString()
    } else {
      updated[index][field] = value
    }
    setOperatingExpenses(updated)
  }

  // Auto-calculate totals
  const totalRevenue = products.reduce((sum, p) => sum + Math.round(parseFloat(p.revenue) || 0), 0)
  const totalCOGS = products.reduce((sum, p) => sum + Math.round(parseFloat(p.cost_of_goods_sold) || 0), 0)
  const totalGrossProfit = totalRevenue - totalCOGS
  const totalExpenses = operatingExpenses.reduce((sum, e) => sum + Math.round(parseFloat(e.amount) || 0), 0)
  const otherIncome = Math.round(parseFloat(plData.other_income) || 0)
  const otherExpenses = Math.round(parseFloat(plData.other_expenses) || 0)
  const netIncome = totalGrossProfit - totalExpenses + otherIncome - otherExpenses

  // Balance sheet calculations
  const calcBS = () => {
    const ca = ['cash_and_equivalents', 'accounts_receivable', 'inventory', 'other_current_assets']
      .reduce((s, f) => s + Math.round(parseFloat(bsData[f]) || 0), 0)
    const fa = Math.round(parseFloat(bsData.fixed_assets) || 0) - Math.round(parseFloat(bsData.accumulated_depreciation) || 0)
    const ta = ca + fa + Math.round(parseFloat(bsData.other_assets) || 0)
    const cl = ['accounts_payable', 'short_term_debt', 'other_current_liabilities']
      .reduce((s, f) => s + Math.round(parseFloat(bsData[f]) || 0), 0)
    const tl = cl + Math.round(parseFloat(bsData.long_term_debt) || 0) + Math.round(parseFloat(bsData.other_liabilities) || 0)
    const te = Math.round(parseFloat(bsData.owner_equity) || 0) + Math.round(parseFloat(bsData.retained_earnings) || 0)
    return { ca, fa, ta, cl, tl, te }
  }
  const bsCalc = calcBS()

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency', currency: 'UGX', minimumFractionDigits: 0
    }).format(amount || 0)
  }

  const handleSubmitPL = async () => {
    if (!selectedMember) { setError('Please select a member first'); return }
    if (!plData.period_start || !plData.period_end) { setError('Please enter the reporting period'); return }
    if (products.some(p => !p.product_name || !p.revenue)) { setError('Please fill in all product details'); return }

    setLoading(true); setError(''); setSuccess('')
    try {
      const response = await apiService.makeAuthenticatedRequest('ADMIN-SUBMIT-FINANCIAL-DATA', {
        member_no: selectedMember.memberNo,
        customer_id: selectedMember.customerId,
        submitted_by_admin: true,
        admin_mg_code: mgKey,
        statement_type: 'profit_loss',
        period_start: plData.period_start,
        period_end: plData.period_end,
        products: products.filter(p => p.product_name),
        operating_expenses: operatingExpenses.filter(e => e.category && e.amount),
        other_income: otherIncome,
        other_expenses: otherExpenses,
        net_income: netIncome,
        notes: plData.notes
      }, true)

      if (response.success || response.status === 'SUCCESS') {
        setSuccess(`Profit & Loss statement submitted successfully for ${selectedMember.fullName}`)
        setProducts([{ product_name: '', revenue: '', cost_of_goods_sold: '', gross_profit: '', notes: '' }])
        setOperatingExpenses([{ category: '', description: '', amount: '' }])
        setPLData({ period_start: '', period_end: '', other_income: '', other_expenses: '', net_income: '', notes: '' })
      } else {
        setError(response.message || 'Failed to submit financial data')
      }
    } catch (err) {
      setError('Failed to submit: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const bsOutOfBalance = Math.abs(bsCalc.ta - (bsCalc.tl + bsCalc.te))

  const handleSubmitBS = async () => {
    if (!selectedMember) { setError('Please select a member first'); return }
    if (!bsData.as_of_date) { setError('Please enter the balance sheet date'); return }
    if (bsOutOfBalance >= 1) {
      setError(`Balance sheet must balance: Assets must equal Liabilities + Equity (within 1 UGX). Currently out by ${formatCurrency(bsOutOfBalance)}.`)
      return
    }

    setLoading(true); setError(''); setSuccess('')
    try {
      const response = await apiService.makeAuthenticatedRequest('ADMIN-SUBMIT-FINANCIAL-DATA', {
        member_no: selectedMember.memberNo,
        customer_id: selectedMember.customerId,
        submitted_by_admin: true,
        admin_mg_code: mgKey,
        statement_type: 'balance_sheet',
        as_of_date: bsData.as_of_date,
        ...bsData,
        total_current_assets: bsCalc.ca,
        net_fixed_assets: bsCalc.fa,
        total_assets: bsCalc.ta,
        total_current_liabilities: bsCalc.cl,
        total_liabilities: bsCalc.tl,
        total_equity: bsCalc.te
      }, true)

      if (response.success || response.status === 'SUCCESS') {
        setSuccess(`Balance Sheet submitted successfully for ${selectedMember.fullName}`)
        setBSData({
          as_of_date: '', cash_and_equivalents: '', accounts_receivable: '', inventory: '',
          other_current_assets: '', total_current_assets: '', fixed_assets: '',
          accumulated_depreciation: '', net_fixed_assets: '', other_assets: '', total_assets: '',
          accounts_payable: '', short_term_debt: '', other_current_liabilities: '',
          total_current_liabilities: '', long_term_debt: '', other_liabilities: '',
          total_liabilities: '', owner_equity: '', retained_earnings: '', total_equity: '', notes: ''
        })
      } else {
        setError(response.message || 'Failed to submit balance sheet')
      }
    } catch (err) {
      setError('Failed to submit: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const expenseCategories = [
    'Rent', 'Salaries & Wages', 'Utilities', 'Transport', 'Communication',
    'Marketing', 'Insurance', 'Maintenance', 'Office Supplies', 'Taxes & Licenses', 'Other'
  ]

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700">
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <FileText className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Financial Data Entry (Admin)</h1>
          </div>
          <p className="text-gray-500 ml-8">Submit financial statements on behalf of members in your group</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">×</button>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-green-700">{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">×</button>
          </div>
        )}

        {/* Selected Member Banner */}
        {selectedMember && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-blue-900">{selectedMember.fullName}</p>
                <p className="text-sm text-blue-600">Member No: {selectedMember.memberNo}</p>
              </div>
            </div>
            <button
              onClick={() => { setSelectedMember(null); setActiveTab('search') }}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Change Member
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
          {[
            { id: 'search', label: 'Find Member', icon: Search },
            { id: 'profit-loss', label: 'Profit & Loss', icon: TrendingUp, disabled: !selectedMember },
            { id: 'balance-sheet', label: 'Balance Sheet', icon: Calculator, disabled: !selectedMember }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : tab.disabled
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search Tab */}
        {activeTab === 'search' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Search for a Member</h2>
            <div className="flex gap-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchMember()}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter member number or name..."
              />
              <button
                onClick={handleSearchMember}
                disabled={searchLoading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {searchLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
                Search
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="mt-6 space-y-3">
                <h3 className="text-sm font-medium text-gray-500">{searchResults.length} member(s) found</h3>
                {searchResults.map((member, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-blue-50 transition">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{member.client_name || member.fullName || member.full_name}</p>
                        <p className="text-sm text-gray-500">#{member.client_id || member.mem_no || member.memberNo}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => selectMember(member)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                    >
                      Select
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profit & Loss Tab */}
        {activeTab === 'profit-loss' && selectedMember && (
          <div className="space-y-6">
            {/* Reporting Period */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Reporting Period</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input type="date" value={plData.period_start}
                    onChange={(e) => setPLData({ ...plData, period_start: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input type="date" value={plData.period_end}
                    onChange={(e) => setPLData({ ...plData, period_end: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>

            {/* Products/Revenue */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Products / Revenue Sources</h2>
                <button onClick={addProduct} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                  <Plus className="w-4 h-4" /> Add Product
                </button>
              </div>
              {products.map((product, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg mb-3 relative">
                  {products.length > 1 && (
                    <button onClick={() => removeProduct(index)} className="absolute top-2 right-2 text-red-400 hover:text-red-600">
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Product Name *</label>
                      <input type="text" value={product.product_name}
                        onChange={(e) => handleProductChange(index, 'product_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g., Maize" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Revenue (UGX) *</label>
                      <input type="number" value={product.revenue}
                        onChange={(e) => handleProductChange(index, 'revenue', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Cost of Goods Sold</label>
                      <input type="number" value={product.cost_of_goods_sold}
                        onChange={(e) => handleProductChange(index, 'cost_of_goods_sold', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Gross Profit</label>
                      <input type="text" value={formatCurrency(parseFloat(product.gross_profit) || 0)} readOnly
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-100 font-medium text-green-700" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Operating Expenses */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Operating Expenses</h2>
                <button onClick={addExpense} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                  <Plus className="w-4 h-4" /> Add Expense
                </button>
              </div>
              {operatingExpenses.map((expense, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg mb-3 relative">
                  {operatingExpenses.length > 1 && (
                    <button onClick={() => removeExpense(index)} className="absolute top-2 right-2 text-red-400 hover:text-red-600">
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                      <select value={expense.category}
                        onChange={(e) => handleExpenseChange(index, 'category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                        <option value="">Select category</option>
                        {expenseCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <input type="text" value={expense.description}
                        onChange={(e) => handleExpenseChange(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g., Monthly rent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Amount (UGX)</label>
                      <input type="number" value={expense.amount}
                        onChange={(e) => handleExpenseChange(index, 'amount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary & Other */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Other Income / Expenses & Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Other Income</label>
                  <input type="number" value={plData.other_income}
                    onChange={(e) => setPLData({ ...plData, other_income: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Other Expenses</label>
                  <input type="number" value={plData.other_expenses}
                    onChange={(e) => setPLData({ ...plData, other_expenses: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea value={plData.notes}
                  onChange={(e) => setPLData({ ...plData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} placeholder="Any additional notes..." />
              </div>

              {/* P&L Summary */}
              <div className="mt-6 bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Total Revenue</span><span className="font-semibold">{formatCurrency(totalRevenue)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Total COGS</span><span className="font-semibold text-red-600">({formatCurrency(totalCOGS)})</span></div>
                <div className="flex justify-between border-t pt-2"><span className="text-gray-600">Gross Profit</span><span className="font-semibold text-green-600">{formatCurrency(totalGrossProfit)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Operating Expenses</span><span className="font-semibold text-red-600">({formatCurrency(totalExpenses)})</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Other Income</span><span className="font-semibold">{formatCurrency(otherIncome)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Other Expenses</span><span className="font-semibold text-red-600">({formatCurrency(otherExpenses)})</span></div>
                <div className="flex justify-between border-t pt-2 text-lg">
                  <span className="font-bold text-gray-900">Net Income</span>
                  <span className={`font-bold ${netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(netIncome)}</span>
                </div>
              </div>

              <button onClick={handleSubmitPL} disabled={loading}
                className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : <Save className="h-5 w-5" />}
                {loading ? 'Submitting...' : `Submit P&L for ${selectedMember.fullName}`}
              </button>
            </div>
          </div>
        )}

        {/* Balance Sheet Tab */}
        {activeTab === 'balance-sheet' && selectedMember && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Balance Sheet Date</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">As of Date *</label>
                <input type="date" value={bsData.as_of_date}
                  onChange={(e) => setBSData({ ...bsData, as_of_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 max-w-xs" />
              </div>
            </div>

            {/* Assets */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Assets</h2>
              <h3 className="text-sm font-medium text-gray-600 mb-3 uppercase tracking-wide">Current Assets</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[
                  { key: 'cash_and_equivalents', label: 'Cash & Equivalents' },
                  { key: 'accounts_receivable', label: 'Accounts Receivable' },
                  { key: 'inventory', label: 'Inventory' },
                  { key: 'other_current_assets', label: 'Other Current Assets' }
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    <input type="number" value={bsData[f.key]}
                      onChange={(e) => setBSData({ ...bsData, [f.key]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                  </div>
                ))}
              </div>
              <div className="bg-green-50 rounded-lg p-3 mb-6">
                <span className="text-sm text-green-700 font-medium">Total Current Assets: {formatCurrency(bsCalc.ca)}</span>
              </div>

              <h3 className="text-sm font-medium text-gray-600 mb-3 uppercase tracking-wide">Fixed Assets</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fixed Assets (Gross)</label>
                  <input type="number" value={bsData.fixed_assets}
                    onChange={(e) => setBSData({ ...bsData, fixed_assets: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Accumulated Depreciation</label>
                  <input type="number" value={bsData.accumulated_depreciation}
                    onChange={(e) => setBSData({ ...bsData, accumulated_depreciation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Other Assets</label>
                  <input type="number" value={bsData.other_assets}
                    onChange={(e) => setBSData({ ...bsData, other_assets: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <span className="text-sm text-blue-700 font-bold">Total Assets: {formatCurrency(bsCalc.ta)}</span>
              </div>
            </div>

            {/* Liabilities */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Liabilities</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[
                  { key: 'accounts_payable', label: 'Accounts Payable' },
                  { key: 'short_term_debt', label: 'Short-term Debt' },
                  { key: 'other_current_liabilities', label: 'Other Current Liabilities' },
                  { key: 'long_term_debt', label: 'Long-term Debt' },
                  { key: 'other_liabilities', label: 'Other Liabilities' }
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    <input type="number" value={bsData[f.key]}
                      onChange={(e) => setBSData({ ...bsData, [f.key]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                  </div>
                ))}
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <span className="text-sm text-red-700 font-medium">Total Liabilities: {formatCurrency(bsCalc.tl)}</span>
              </div>
            </div>

            {/* Equity */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Owner's Equity</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Owner's Equity / Capital</label>
                  <input type="number" value={bsData.owner_equity}
                    onChange={(e) => setBSData({ ...bsData, owner_equity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Retained Earnings</label>
                  <input type="number" value={bsData.retained_earnings}
                    onChange={(e) => setBSData({ ...bsData, retained_earnings: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 mb-4">
                <span className="text-sm text-emerald-700 font-medium">Total Equity: {formatCurrency(bsCalc.te)}</span>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea value={bsData.notes}
                  onChange={(e) => setBSData({ ...bsData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>

              {/* Balance Check */}
              <div className={`mt-4 p-4 rounded-lg ${Math.abs(bsCalc.ta - (bsCalc.tl + bsCalc.te)) < 1 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <p className="text-sm font-medium">
                  {Math.abs(bsCalc.ta - (bsCalc.tl + bsCalc.te)) < 1
                    ? '✅ Balance sheet balances! Assets = Liabilities + Equity'
                    : `⚠️ Out of balance by ${formatCurrency(Math.abs(bsCalc.ta - (bsCalc.tl + bsCalc.te)))}`}
                </p>
              </div>

              <button onClick={handleSubmitBS} disabled={loading}
                className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : <Save className="h-5 w-5" />}
                {loading ? 'Submitting...' : `Submit Balance Sheet for ${selectedMember.fullName}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminFinancialDataEntry
